import { useEffect, useRef, useState } from "react";
import { Globe, Check } from "lucide-react";
import { useT } from "@/contexts/LanguageContext";

export default function LanguageSwitcher({ dense = false }) {
  const { lang, setLang, languages, t } = useT();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const current = languages.find((l) => l.code === lang) || languages[0];

  return (
    <div className="relative" ref={rootRef} data-testid="language-switcher">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={t("lang.choose")}
        className={`inline-flex items-center gap-2 rounded-sm border border-[#E4E7EC] hover:border-[#C9A45C] transition-colors ${
          dense ? "px-2 py-1.5 text-xs" : "px-3 py-2 text-sm"
        } text-[#172033] hover:text-[#9A762F]`}
        data-testid="language-switcher-btn"
      >
        <Globe size={dense ? 14 : 16} className="text-[#9A762F]" />
        <span className="hidden sm:inline uppercase tracking-[0.2em] text-[10px]">
          {current.code}
        </span>
        <span className="sm:hidden text-sm">{current.flag}</span>
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-56 ph-card rounded-sm shadow-xl z-50 py-2"
          role="menu"
          data-testid="language-switcher-menu"
        >
          <div className="px-3 py-2 text-[10px] tracking-[0.3em] uppercase text-[#9A762F] border-b border-[#E4E7EC]">
            {t("lang.choose")}
          </div>
          {languages.map((l) => (
            <button
              key={l.code}
              onClick={() => {
                setLang(l.code);
                setOpen(false);
              }}
              className={`w-full text-left px-3 py-2 flex items-center justify-between text-sm hover:bg-[#F8F9FB] transition-colors ${
                l.code === lang ? "text-[#9A762F]" : "text-[#172033]"
              }`}
              data-testid={`language-option-${l.code}`}
            >
              <span className="flex items-center gap-2">
                <span className="text-base leading-none">{l.flag}</span>
                {l.label}
              </span>
              {l.code === lang && <Check size={14} className="text-[#9A762F]" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
